import { api } from '../libs/api'
import { getRequestConfig } from '../utils/auth'

export interface User {
  id: string
  authId?: string
  email: string
  firstName?: string
  lastName?: string
  phone?: string
  role: 'admin' | 'user'
  roles: string[]
  isActive: boolean
  billing?: {
    companyName?: string
    billingEmail?: string
    address?: string
    city?: string
    postalCode?: string
    country?: string
    vatNumber?: string
  }
  createdAt: string
  updatedAt: string
}

export interface UpdateUserData {
  firstName?: string
  lastName?: string
  phone?: string
  isActive?: boolean
}

export interface CreateUserData {
  email: string
  firstName?: string
  lastName?: string
  phone?: string
  role?: 'admin' | 'user'
}

export interface UserResponse {
  success: boolean
  data: User
}

export interface UsersResponse {
  success: boolean
  data: User[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
}

export interface UpdateUserResponse {
  success: boolean
  data: User
  message: string
}

class UserService {
  private readonly baseUrl = '/auth'

  async getCurrentUser(): Promise<User> {
    try {
      const response = await api.get(`${this.baseUrl}/me`, getRequestConfig()) as UserResponse

      if (!response.success) {
        throw new Error('Failed to fetch current user')
      }

      return response.data
    } catch (error) {
      console.error('Error fetching current user:', error)
      throw error
    }
  }

  async getAllUsers(params?: {
    page?: number
    limit?: number
    role?: string
    search?: string
  }): Promise<UsersResponse> {
    try {
      const queryParams = new URLSearchParams()
      if (params?.page) queryParams.append('page', params.page.toString())
      if (params?.limit) queryParams.append('limit', params.limit.toString())
      if (params?.role) queryParams.append('role', params.role)
      if (params?.search) queryParams.append('search', params.search)

      const response = await api.get(`/users?${queryParams.toString()}`, getRequestConfig()) as UsersResponse

      if (!response.success) {
        throw new Error('Failed to fetch users')
      }

      return response
    } catch (error) {
      console.error('Error fetching users:', error)
      throw error
    }
  }

  async getUserById(userId: string): Promise<User> {
    try {
      const response = await api.get(`/users/${userId}`, getRequestConfig()) as UserResponse

      if (!response.success) {
        throw new Error('Failed to fetch user')
      }

      return response.data
    } catch (error) {
      console.error('Error fetching user:', error)
      throw error
    }
  }

  async updateUser(userId: string, userData: UpdateUserData): Promise<User> {
    try {
      const response = await api.put(`/users/${userId}`, userData, getRequestConfig()) as UpdateUserResponse

      if (!response.success) {
        throw new Error('Failed to update user')
      }

      return response.data
    } catch (error) {
      console.error('Error updating user:', error)
      throw error
    }
  }

  async createUser(userData: CreateUserData): Promise<User> {
    try {
      const response = await api.post(`/users`, userData, getRequestConfig()) as UserResponse

      if (!response.success) {
        throw new Error('Failed to create user')
      }

      return response.data
    } catch (error) {
      console.error('Error creating user:', error)
      throw error
    }
  }
}

// Create and export the service instance
export const userService = new UserService()
export default userService 